import { formatFileSize } from '../i18n/fileSize';
import { Trans, useLingui } from '@lingui/react/macro';
import { useRef } from 'react';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { ImageViewer } from './viewers/ImageViewer';
import { MarkdownViewer } from './viewers/MarkdownViewer';
import { CodeViewer, isCodeFile } from './viewers/CodeViewer';
import { TextViewer } from './viewers/TextViewer';

function isTextMime(mime: string): boolean {
  return mime.startsWith('text/') || mime === 'application/json' || mime === 'application/xml';
}



export function RemoteFileViewer({
  name,
  content,
  mimeType,
  size,
  onClose,
}: {
  name: string;
  content: string;
  mimeType: string;
  size: number;
  onClose: () => void;
}) {
  const { t, i18n } = useLingui();
  const isMd = name.endsWith('.md');
  const isCode = isCodeFile(name);
  const isImage = mimeType.startsWith('image/');
  const isText = isTextMime(mimeType) || isMd || isCode;
  const overlayRef = useRef<HTMLDivElement>(null);

  useDismissibleLayer({ active: true, within: overlayRef, onEscape: onClose });

  const renderContent = () => {
    if (isImage) {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      return <ImageViewer url={url} name={name} />;
    }
    if (isMd) return <MarkdownViewer content={content} />;
    if (isCode) return <CodeViewer content={content} filename={name} />;
    if (isText) return <TextViewer content={content} />;
    return (
      <div className="file-viewer-binary">
        <p><Trans>Preview is not supported for this file type</Trans></p>
        <p className="file-viewer-meta">{name} · {formatFileSize(i18n, size)}</p>
      </div>
    );
  };

  return (
    <div ref={overlayRef} className="file-viewer-overlay" onClick={onClose}>
      <div className="file-viewer-panel" onClick={e => e.stopPropagation()}>
        <div className="file-viewer-header">
          <span className="file-viewer-title">{name}</span>
          <span className="file-viewer-size">{formatFileSize(i18n, size)}</span>
          <button aria-label={t`Close file preview`} className="file-viewer-close" onClick={onClose}>✕</button>
        </div>
        <div className="file-viewer-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
